<script setup>
import { useStore } from 'vuex';
import { ref, computed, reactive, onBeforeMount } from 'vue';
import ChatAPIService from '@/js/api/chats/chats.js';
import { getDatabase, ref as dbRef, onValue } from 'firebase/database';

const searchTerm = ref('');
const store = useStore();



const fetchUsers = async () => {
    let users = [];
    try {
        const response = await ChatAPIService.getUsers();
        const db = getDatabase();
        const brandId = store.getters.brand?.id;
        const brandstatusRef = dbRef(db, `brand-${brandId}`);

        response.data.customers.forEach(customer => {
            const contactPersons = customer.contact_persons || [];

            contactPersons.forEach(contact => {
                const userKey = `customers-${contact.id}`;
                
                const user = {
                    type: 'customers',
                    id: contact.id,
                    customerid: contact.contact_type,
                    name: contact.name,
                    role: customer.company_name + ' - Customer',
                    avatar: contact.name?.split(" ").map(word => word.charAt(0)).join(""),
                    lastMessage: 'No messages yet',
                    timestamp: 'Just now',
                    dateLabel: '',
                    timestampRaw: 0,
                    unreadCount: 0,
                    isOnline: false,
					availability: 'offline',
                    messages: [],
                };

                listenToUserStatus(userKey, user);
                listenToUnreadMessages(userKey, user);
                users.push(user);
            });
        });

        response.data.staffs?.forEach(staff => {
            const userKey = `staffs-${staff.id}`;
            const user = {
                type: 'staffs',
                id: staff.id,
                name: staff.name,
                role: staff.user_role?.display_name + ' - Staff' || staff.title + ' - Staff',
                avatar: staff.name.split(" ").map(word => word.charAt(0)).join(""),
                lastMessage: 'No messages yet',
                timestamp: 'Just now',
                dateLabel: '',
                timestampRaw: 0,
                unreadCount: 0,
                isOnline: false,
				availability: 'offline',
                messages: [],
            };
            listenToUserStatus(userKey, user);

            listenToUnreadMessages(userKey, user);
            users.push(user);
        });

        if (response.data?.owner) {
            const userKey = `owner-${response.data.owner.id}`;
            const user = {
                type: 'owner',
                id: response.data.owner.id,
                name: response.data.owner.name,
                role: 'Owner',
                avatar: response.data.owner.name.split(" ").map(word => word.charAt(0)).join(""),
                lastMessage: 'No messages yet',
                timestamp: 'Just now',
                dateLabel: '',
                timestampRaw: 0,
                unreadCount: 0,
                isOnline: false,
				availability: 'offline',
                messages: [],
            };


            listenToUserStatus(userKey, user);
            listenToUnreadMessages(userKey, user);
            users.push(user);
        }

        return users || [];
    } catch (error) {
        return [];
    }
};

const listenToUserStatus = (userKey, userObj) => {
    const db = getDatabase();
    const brandId = store.getters.brand?.id;
    const statusRef = dbRef(db, `brand-${brandId}/users/${userKey}`);

    onValue(statusRef, (snapshot) => {
        const presence = snapshot.val() || {};
		const hasActiveConnection = Boolean(
			presence.connections && Object.keys(presence.connections).length
		);
		const isCustomer = userKey.startsWith('customers-');
		const currentAvailability = hasActiveConnection && (isCustomer || presence.availability === 'active')
			? 'active'
			: 'offline';
        const isNowOnline = currentAvailability === 'active';
        // Force reactivity
        if (userObj.isOnline !== isNowOnline || userObj.availability !== currentAvailability) {
            userObj.isOnline = isNowOnline;
			userObj.availability = currentAvailability;
            
            // ✅ Manually trigger update (deep reactivity fix)
            const index = users.findIndex(u => u.id === userObj.id && u.type === userObj.type);
            if (index !== -1) {
                users.splice(index, 1, { ...userObj }); // force update
            }
        }
    });
};


const listenToUnreadMessages = (userKey, userObj) => {
    const db = getDatabase();
    const brandId = store.getters.brand?.id;
    const currentRole = store.getters.role.split('-')[0];
    const currentId = store.getters.user.user.role?.id;
    const mappedType = currentRole === 'staff' ? 'staffs' : currentRole === 'customer' ? 'customers' : currentRole;
    const currentUserKey = `${mappedType}-${currentId}`;
    
    // ✅ Always sort to get the real chatId format used in Firebase
    const sortedParticipants = [currentUserKey, userKey].sort();
    const chatId = `${sortedParticipants[0]}-${sortedParticipants[1]}`;
    
    const chatsRef = dbRef(db, `brand-${brandId}/chats/${chatId}`);


    onValue(chatsRef, (snapshot) => {
        const chat = snapshot.val();
        let unread = 0;
        let latestMessage = 'No messages yet';
        let latestTimestamp = '';
        let latestTimestampRaw = 0;

        if (chat) {
            const messages = chat.messages || {};
            const readBy = chat.participants?.[userKey]?.read;

            for (const messageId in messages) {
                const msg = messages[messageId];
                const currentRole = store.getters.role;
                const currentId = store.getters.user.user.role?.id;
                let currentUserKey;
                if (currentRole === 'staff') {
                    currentUserKey = `staffs-${currentId}`;
                } else if (currentRole === 'customer') {
                    currentUserKey = `customers-${currentId}`;
                } else {
                    if(currentRole.includes('customer')) {
                        currentUserKey = `${currentRole.split('-')[0]}s-${currentId}`;
                    }
                    else{
                        currentUserKey = `${currentRole.split('-')[0]}-${currentId}`;
                    }
                }
                if (msg.readBy && msg.readBy[currentUserKey] === false) {
                    unread++;
                }
            }

            if (chat.lastMessage) {
                latestMessage = chat.lastMessage.text;
                // latestTimestamp = formatTime(chat.lastMessage.timestamp);
                latestTimestampRaw = chat.lastMessage.timestamp || 0;
                latestTimestamp = formatTime(latestTimestampRaw);
            }
        }

        userObj.unreadCount = unread;
        userObj.lastMessage = latestMessage;
        userObj.timestamp = latestTimestamp;
        userObj.dateLabel = formatDateLabel(latestTimestampRaw);
        userObj.timestampRaw = latestTimestampRaw;
        forceUserUpdate(userObj);
    });
};


const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

const formatDateLabel = (timestamp) => {
    if (!timestamp) return '';

    const messageDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (first, second) =>
        first.getFullYear() === second.getFullYear()
        && first.getMonth() === second.getMonth()
        && first.getDate() === second.getDate();

    if (isSameDay(messageDate, today)) return 'Today';
    if (isSameDay(messageDate, yesterday)) return 'Yesterday';

    return messageDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
};

const users = reactive([]);
const openChat = (user) => {
    user.unreadCount = 0; // Reset unread count when opening chat
    store.dispatch('openChat', user);
};

const filteredChats = computed(() => {
    return users
        .filter(chat =>
            (chat.name.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
                chat.role.toLowerCase().includes(searchTerm.value.toLowerCase()))
        )
        .sort((a, b) => {
            // Sort by timestamp (most recent first)
            // If timestamps are equal, prioritize unread messages
            if (b.timestampRaw !== a.timestampRaw) {
                return b.timestampRaw - a.timestampRaw;
            }
            // If same timestamp, put unread messages first
            return b.unreadCount - a.unreadCount;
        });
});

onBeforeMount(async () => {
    const fetchedUsers = await fetchUsers();
    users.splice(0, users.length, ...fetchedUsers);
});

const forceUserUpdate = (userObj) => {
    const index = users.findIndex(u => u.id === userObj.id && u.type === userObj.type);
    if (index !== -1) {
        // Remove the old user and add the updated one
        // The computed filteredChats will automatically re-sort it
        users.splice(index, 1, { ...userObj });
    }
};
</script>

<template>
    <div class="chat-container">
        <!-- Chat List View -->
        <div class="chat-list-view">
            <!--begin:Search-->
            <div class="input-group input-group-solid mb-5">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <span class="svg-icon svg-icon-lg">
                            <!--begin::Svg Icon | path:/metronic/theme/html/demo1/dist/assets/media/svg/icons/General/Search.svg-->
                            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                                width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
                                <title>Stockholm-icons / General / Search</title>
                                <desc>Created with Sketch.</desc>
                                <defs></defs>
                                <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                    <rect x="0" y="0" width="24" height="24"></rect>
                                    <path
                                        d="M14.2928932,16.7071068 C13.9023689,16.3165825 13.9023689,15.6834175 14.2928932,15.2928932 C14.6834175,14.9023689 15.3165825,14.9023689 15.7071068,15.2928932 L19.7071068,19.2928932 C20.0976311,19.6834175 20.0976311,20.3165825 19.7071068,20.7071068 C19.3165825,21.0976311 18.6834175,21.0976311 18.2928932,20.7071068 L14.2928932,16.7071068 Z"
                                        fill="#000000" fill-rule="nonzero" opacity="0.3"></path>
                                    <path
                                        d="M11,16 C13.7614237,16 16,13.7614237 16,11 C16,8.23857625 13.7614237,6 11,6 C8.23857625,6 6,8.23857625 6,11 C6,13.7614237 8.23857625,16 11,16 Z M11,18 C7.13400675,18 4,14.8659932 4,11 C4,7.13400675 7.13400675,4 11,4 C14.8659932,4 18,7.13400675 18,11 C18,14.8659932 14.8659932,18 11,18 Z"
                                        fill="#000000" fill-rule="nonzero"></path>
                                </g>
                            </svg><!--end::Svg Icon-->
                        </span>
                    </span>
                </div>
                <input type="text" placeholder="Search conversations by name & role..." v-model="searchTerm"
                    class="form-control py-4 h-auto">
            </div>
            <!--end:Search-->

            <!-- Chat Users List -->
            <div class="chat-users-list">
                <div v-for="(chat, index) in filteredChats" :key="index" @click="openChat(chat)" class="chat-user-item"
                    :class="{
                        'secondary-customer': chat.customerid === 'secondary',
                        'primary-customer': chat.customerid !== 'secondary'
                    }">
                    <div class="user-avatar-container">
                        <div class="user-avatar">
                            {{ chat.avatar }}
                        </div>
						<div v-if="chat.availability === 'active'" class="online-status" title="Active"></div>
						<div v-else class="offline-status" title="Offline"></div>
                    </div>

                    <div class="user-info">
                        <div class="user-header">
                            <h3 class="user-name">{{ chat.name }}</h3>
                            <div v-if="chat.timestampRaw" class="message-date-time">
                                <span class="message-date">{{ chat.dateLabel }}</span>
                                <span class="message-time">{{ chat.timestamp }}</span>
                            </div>
                        </div>
                        <p class="last-message">{{ chat.lastMessage }}</p>
                        <!-- <p class="user-role">{{ chat.role }}</p> -->
                    </div>

                    <div v-if="chat.unreadCount > 0" class="unread-count">
                        {{ chat.unreadCount }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
.chat-container {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.chat-list-view {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.chat-users-list {
    flex: 1;
    overflow-y: auto;
    height: 555px;
    width: 100%;
    position: absolute;
    top: 60px;
}

.chat-user-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid #f0f0f0;
}

.chat-user-item:hover {
    background-color: #f8f9fa;
}

.chat-user-item:last-child {
    border-bottom: none;
}

.user-avatar-container {
    position: relative;
    margin-right: 12px;
}

.user-avatar {
    width: 45px;
    height: 45px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 16px;
}

.online-status {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    background-color: #28a745;
    border: 2px solid white;
    border-radius: 50%;
    transition: background-color 0.3s ease;
}

.offline-status {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    background-color: #7e7c7c;
    border: 2px solid white;
    border-radius: 50%;
    transition: background-color 0.3s ease;
}


.user-info {
    flex: 1;
    min-width: 0;
}

.user-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.user-name {
    font-weight: 600;
    color: #333;
    font-size: 15px;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.message-time {
    margin-top: 3px;
    font-size: 12px;
    color: #888;
    white-space: nowrap;
}

.message-date-time {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: center;
    min-width: 72px;
    text-align: center;
    line-height: 1.2;
}

.message-date {
    color: #a1a5b7;
    font-size: 10px;
    white-space: nowrap;
}

.last-message {
    font-size: 13px;
    color: #666;
    margin: 0 0 4px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.user-role {
    font-size: 11px;
    color: #999;
    margin: 0;
}

.unread-count {
    background-color: #007bff;
    color: white;
    font-size: 11px;
    font-weight: 600;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
}

.secondary-customer {
    margin-left: 2rem;
    padding-left: 1rem;
    border-left: 3px solid #e3f2fd;
    background-color: #f8f9fa;
    position: relative;
}

.secondary-customer::before {
    content: '└─';
    position: absolute;
    left: -0.5rem;
    color: #6c757d;
    font-weight: bold;
}

.primary-customer {
    font-weight: 600;
}
</style>
